import * as React from 'react'
import {
  filterOptions,
  firstEnabledValue,
  flattenOptions,
  type SelectMatchMode,
  type SelectOptions,
} from './select-option-utils'
import { useSelectDropdownPosition } from './use-select-dropdown-position'
import { useSelectKeyboardNavigation } from './use-select-keyboard-navigation'

/** About six 44px rows (plus the search box when there is one), then the list scrolls. */
const MAX_LIST_HEIGHT = 288
const MAX_LIST_HEIGHT_WITH_SEARCH = 340

interface SelectListStateParams {
  selectId: string
  options: SelectOptions
  value?: string
  onChange?: (value: string) => void
  searchable: boolean
  matchMode: SelectMatchMode
  /** Disabled or loading: the list can't open, and an open one closes. */
  isDisabled: boolean
  growToContent: boolean
  containerRef: React.RefObject<HTMLDivElement | null>
  popoverRef: React.RefObject<HTMLDivElement | null>
  triggerRef: React.RefObject<HTMLButtonElement | null>
  searchInputRef: React.RefObject<HTMLInputElement | null>
}

/**
 * Open/close, search, highlighted row, placement and keyboard wiring for `Select`.
 * Focus rules: picking, Esc and Tab hand focus back to the trigger; an outside click
 * leaves focus where it lands; a searchable list focuses its search box once per open.
 */
export function useSelectListState({
  selectId,
  options,
  value,
  onChange,
  searchable,
  matchMode,
  isDisabled,
  growToContent,
  containerRef,
  popoverRef,
  triggerRef,
  searchInputRef,
}: SelectListStateParams) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [searchQuery, setSearchQuery] = React.useState('')
  // Highlighted row + a counter bumped by every keyboard move, so each move scrolls the row into
  // view exactly once (hover moves the highlight without asking to scroll).
  const [activeState, setActiveState] = React.useState<{ value: string | null; scrollRequest: number }>({
    value: null,
    scrollRequest: 0,
  })
  const handledScrollRequestRef = React.useRef(0)

  const allOptions = React.useMemo(() => flattenOptions(options), [options])
  const filteredOptions = React.useMemo(
    () => filterOptions(options, searchQuery, matchMode),
    [options, searchQuery, matchMode]
  )
  const visibleOptions = React.useMemo(() => flattenOptions(filteredOptions), [filteredOptions])

  // Stable DOM ids per option (by position, so any value string is safe in an id).
  const optionIndex = React.useMemo(() => new Map(allOptions.map((opt, index) => [opt.value, index])), [allOptions])
  const optionId = (optionValue: string) => `${selectId}-opt-${optionIndex.get(optionValue) ?? 'x'}`
  const activeValue =
    activeState.value !== null && visibleOptions.some((o) => o.value === activeState.value) ? activeState.value : null
  const activeDescendant = isOpen && activeValue !== null ? optionId(activeValue) : undefined

  const setActiveValue = React.useCallback((next: string | null, opts?: { scroll?: boolean }) => {
    setActiveState((prev) =>
      opts?.scroll
        ? { value: next, scrollRequest: prev.scrollRequest + 1 }
        : prev.value === next
          ? prev
          : { value: next, scrollRequest: prev.scrollRequest }
    )
  }, [])

  const position = useSelectDropdownPosition(triggerRef, popoverRef, {
    isOpen,
    growToContent,
    maxListHeight: searchable ? MAX_LIST_HEIGHT_WITH_SEARCH : MAX_LIST_HEIGHT,
  })
  const isPlaced = position !== null

  const openMenu = () => {
    if (isDisabled) return
    setSearchQuery('')
    const current = allOptions.find((o) => o.value === value && !o.disabled)
    setActiveValue(current ? current.value : firstEnabledValue(allOptions), { scroll: true })
    setIsOpen(true)
    // Safari does not focus a clicked button; the trigger must hold focus to drive the list.
    if (!searchable) triggerRef.current?.focus({ preventScroll: true })
  }

  const closeMenu = React.useCallback(
    (refocus: boolean) => {
      setIsOpen(false)
      setSearchQuery('')
      if (refocus) triggerRef.current?.focus()
    },
    [triggerRef]
  )

  // Re-picking the current value is a no-op (handlers with side effects must not re-run).
  const selectValue = (optionValue: string) => {
    if (optionValue !== value) onChange?.(optionValue)
    closeMenu(true)
  }

  const setQuery = (query: string) => {
    setSearchQuery(query)
    setActiveValue(firstEnabledValue(flattenOptions(filterOptions(options, query, matchMode))), { scroll: true })
  }

  const { onTriggerKeyDown, onSearchKeyDown } = useSelectKeyboardNavigation({
    isOpen,
    allOptions,
    visibleOptions,
    value,
    activeValue,
    setActiveValue,
    openMenu,
    closeMenu,
    selectValue,
    changeValue: (next) => onChange?.(next),
  })

  // Close on a click outside the trigger and the (portalled) popover; focus stays where it lands.
  React.useEffect(() => {
    if (!isOpen) return
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      if (containerRef.current?.contains(target) || popoverRef.current?.contains(target)) return
      closeMenu(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen, closeMenu, containerRef, popoverRef])

  // A disabled/loading trigger cannot keep a list open.
  React.useEffect(() => {
    if (isDisabled && isOpen) closeMenu(false)
  }, [isDisabled, isOpen, closeMenu])

  // Searchable: focus moves into the search box once per open, when the popover is placed.
  React.useEffect(() => {
    if (isOpen && searchable && isPlaced) searchInputRef.current?.focus()
  }, [isOpen, searchable, isPlaced, searchInputRef])

  // Each keyboard move (and opening) brings the highlighted row into view once, before paint.
  React.useLayoutEffect(() => {
    if (!isOpen || !isPlaced || activeState.scrollRequest === handledScrollRequestRef.current) return
    handledScrollRequestRef.current = activeState.scrollRequest
    if (activeValue !== null) document.getElementById(optionId(activeValue))?.scrollIntoView?.({ block: 'nearest' })
  })

  return {
    isOpen,
    searchQuery,
    setQuery,
    allOptions,
    filteredOptions,
    activeValue,
    activeDescendant,
    setActiveValue,
    optionId,
    position,
    openMenu,
    closeMenu,
    selectValue,
    onTriggerKeyDown,
    onSearchKeyDown,
  }
}
